import { Alert, Box, Typography } from "@mui/material";
import SurveyCharts from "pages/dashboard/CampaignStatistics/ColumnChart";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { handleDownload } from "shared/helpers";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";
import useTranslation from "shared/helpers/hooks/useTranslation";
import BasicAutocomplete from "shared/ui/Autocomplete";
import { ERequestStatus } from "store/enums/index.enum";
import {
  ExportSurveyResponses,
  GetAllPinnedSurveys,
  UnPinAllSurveys,
} from "store/slicers/campaignDetail";
import { GetCampaigns, selectCampaigns } from "store/slicers/campaigns";
import { defaultCampaignListRequestData } from "../constants";
import { FormProvider, useForm } from "react-hook-form";
import { ICampaign } from "store/interfaces/campaigns";
import { ISurveysStatisticsGridResponse } from "store/interfaces/campaignDetails";

interface IFormData {
  campaign: ICampaign | null;
}

interface IPinnedSurvey extends Pick<
  ISurveysStatisticsGridResponse,
  "campaignID" | "campaignName"
> {
  surveys: ISurveysStatisticsGridResponse;
}

const PinnedSurveys = () => {
  const campaignList = useSelector(selectCampaigns);

  const [pinnedSurveys, setPinnedSurveys] = useState<IPinnedSurvey[] | null>(
    null
  );
  const [isAutocompleteLoading, setAutocompleteLoading] = useState(false);

  // TODO: Реализация после новой ручки
  // const [groupedPinnedSurveys, setGroupedPinnedSurveys] = useState([]);

  const form = useForm<IFormData>({
    defaultValues: {
      campaign: null,
    },
  });

  const dispatch = useAsyncDispatch();
  const t = useTranslation();

  const onExport = async (id) => {
    const { meta, payload } = await dispatch(ExportSurveyResponses(Number(id)));

    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      return;
    }
    handleDownload(payload);
  };

  const getCampaignList = async (search: string) => {
    try {
      setAutocompleteLoading(true);
      const data = {
        ...defaultCampaignListRequestData,
        searchText: search,
      };
      await dispatch(GetCampaigns(data));
    } catch (error) {
      setAutocompleteLoading(false);
    } finally {
      setAutocompleteLoading(false);
    }
  };

  const unPinSurveys = async (id: number) => {
    const { meta } = await dispatch(UnPinAllSurveys(Number(id)));
    if (meta.requestStatus !== "fulfilled") {
      return;
    }
    toast.success(t("success"));
    setPinnedSurveys(null);
    form.reset();

    // TODO: Реализация после новой ручки
    // onGroupPinnedSurveys(0);
  };

  const groupByCampaign = (data): IPinnedSurvey[] => {
    return Object.values(
      data.reduce((acc, survey) => {
        const { campaignID, campaignName } = survey;

        if (!acc[campaignID]) {
          acc[campaignID] = {
            campaignID,
            campaignName,
            surveys: [],
          };
        }

        acc[campaignID].surveys.push(survey);
        return acc;
      }, {})
    );
  };

  // TODO: Реализация после новой ручки
  // const onGroupPinnedSurveys = useCallback(
  //   async (campaignId?: number) => {
  //     const { meta, payload } = await dispatch(GetAllPinnedSurveys(campaignId));
  //     if (meta.requestStatus !== "fulfilled") {
  //       return;
  //     }
  //     const list = groupByCampaign(payload || []);
  //     setGroupedPinnedSurveys(list);
  //   },
  //   [dispatch]
  // );

  const handleChange = async () => {
    const selectedCampaign = form.getValues("campaign");

    if (!selectedCampaign) {
      return;
    }

    const { meta, payload } = await dispatch(
      GetAllPinnedSurveys(selectedCampaign.id)
    );

    if (meta.requestStatus !== "fulfilled") {
      return;
    }

    const list = groupByCampaign(payload || []);
    setPinnedSurveys(list);
  };

  const init = useCallback(async () => {
    // TODO: Реализация после новой ручки
    // onGroupPinnedSurveys(0);

    await dispatch(GetCampaigns(defaultCampaignListRequestData));
  }, [dispatch]);

  useEffect(() => {
    init();
  }, [init]);

  const pinnedSurvey = pinnedSurveys?.[0];

  return (
    <FormProvider {...form}>
      <Typography variant="h4" my={4} color="text.secondary">
        {t("custom_questions")}
      </Typography>

      <Box mb={4}>
        <BasicAutocomplete
          inputLabel="Campaign"
          options={campaignList?.displayData || []}
          name="campaign"
          defaultValue={null}
          optionLabel="name"
          async
          loading={isAutocompleteLoading}
          fetchFn={getCampaignList}
          onChangeCB={handleChange}
        />
      </Box>

      {!!pinnedSurveys && !pinnedSurveys.length && (
        <Alert sx={{ mt: 2 }} severity="info">
          No results found. Please select another campaign
        </Alert>
      )}

      {!!pinnedSurvey && (
        <Box mx={2}>
          <SurveyCharts
            init={handleChange}
            data={pinnedSurvey.surveys}
            campaignName={pinnedSurvey.campaignName}
            onExport={() => onExport(pinnedSurvey.campaignID)}
            areAllSurveysPinned={true}
            pinAllSurvey={() => unPinSurveys(pinnedSurvey.campaignID)}
          />
        </Box>
      )}

      {/* TODO: Реализация после новой ручки */}
      {/* {groupedPinnedSurveys && (
        <Box mx={2}>
          {groupedPinnedSurveys?.map((i: any) => {
            return (
              <Box key={i.campaignID} mb={4}>
                <Box>
                  <SurveyCharts
                    init={() => onGroupPinnedSurveys(0)}
                    data={i.surveys}
                    campaignName={i.campaignName}
                    onExport={() => onExport(i.campaignID)}
                    areAllSurveysPinned={true}
                    pinAllSurvey={() => unPinSurveys(i.campaignID)}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      )} */}
    </FormProvider>
  );
};

export default PinnedSurveys;
